import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/form";
import { toast } from "react-toastify";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Multiselect } from "react-widgets";
import moment from "moment";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ExpandDiv from "../common/ExpandDiv";
import ShareModal from "../common/ShareModal";
import {
  Select,
  Checkbox,
  FieldTable,
  Input,
  RadioButton,
  TextArea,
} from "../common/ControlBundle";
import {
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  sendShareEmail,
  handleFieldTableChange,
  handleAddFieldTableRow,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  ArrayCheck,
} from "../../scripts/helperScripts";
import {
  getProgramUnits,
  getServiceItems,
  writeProcessLog,
} from "../services/soar";
import { getSchema } from "./joi/validation";
import logo from "../../assets/logo.png";

function SystemChangeRequest() {
  const navigate = useNavigate();

  //Get route and query string params
  const location = useLocation();
  const { formid, submissionid } = useParams();
  const qs = queryString.parse(location);
  console.log("Params", formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid); // Latest Form Version Id
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  //Get Client Info
  useEffect(() => {
    if (!submissionid) {
      return;
    }

    const loadSubmission = async () => {
      const response = await getSubmission(submissionid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setData(response.Data);
      setFormsyDetails(response.FormsyDetails);

      if (response.Comments) {
        setComments(response.Comments);
      }

      if (response.Signatures) {
        setSignatures(response.Signatures);
      }
    };

    loadSubmission();
  }, [submissionid]);

  useEffect(() => {
    if (
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: "",
          ContactName: "",
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [formid, loadingConfig, safyInfo?.user, formsyConfig, submissionid]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);

    if (
      getFieldValue(data.RequestType) === "New Program" ||
      getFieldValue(data.RequestType) === "Service"
    ) {
      const serviceItems = JSON.parse(getFieldValue(data.ServiceItems));

      if (serviceItems.length === 0 || !serviceItems[0].Name) {
        results.ServiceItems = "Missing Service Item information...";
      }
    }

    if (
      getFieldValue(data.RequestType) === "New Program" ||
      getFieldValue(data.RequestType) === "Modality"
    ) {
      if (getFieldValue(data.ModalityRequestType) === "Adding a new modality") {
        const modalities = JSON.parse(getFieldValue(data.ModalityNewDetails));

        if (modalities.length === 0 || !modalities[0].Name) {
          results.ModalityNewDetails = "Missing New Modality details...";
        }
      }
    }

    if (getFieldValue(data.RequestType) === "Progress Note Template") {
      const templates = JSON.parse(getFieldValue(data.ProgressNoteTemplates));

      if (
        templates.length === 0 ||
        !templates[0].Template ||
        !templates[0].ServiceItems
      ) {
        results.ProgressNoteTemplates =
          "Missing Progress Note Template details...";
      }
    }

    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (
      formsyDetailsObj.FinalSigned !== "1" &&
      safyInfo.user.azureId.toUpperCase() ===
        formsyDetails.CreatedById.toUpperCase()
    ) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(undefined);
  const [programUnitOptions, setProgramUnitOptions] = useState([]);
  const [loadingProgramUnits, setLoadingProgramUnits] = useState(false);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [loadingServiceItems, setLoadingServiceItems] = useState(false);

  const handleMultiselectChange = (fieldName, fieldValue) => {
    const e = {
      currentTarget: {
        name: fieldName,
        value: fieldValue,
        type: "multiselect",
      },
    };
    handleChange(e);
  };

  const userBusinessUnit = useMemo(() => {
    if (!safyInfo.user?.safyInfo?.BusinessUnitIdName) {
      return "";
    }

    return safyInfo.user.safyInfo.BusinessUnitIdName;
  }, [safyInfo.user]);

  const userDivisions = useMemo(() => {
    if (!safyInfo.user?.divisions) {
      return [];
    }

    return safyInfo.user.divisions.map((division) => {
      return { _id: division, name: division };
    });
  }, [safyInfo.user]);

  useEffect(() => {
    const loadProgramUnits = async () => {
      setLoadingProgramUnits(true);
      const response = await getProgramUnits(userBusinessUnit);

      if (!isMountedRef.current || !response) {
        return;
      }

      const options = response
        .map((unit) => {
          return {
            _id: unit.ccx_name,
            name: unit.ccx_name,
            company: getFieldName(unit.owningbusinessunit),
          };
        })
        .sort((a, b) => {
          const companyA = a.company.toUpperCase();
          const companyB = b.company.toUpperCase();
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();

          if (nameA < nameB) {
            if (companyA < companyB) {
              return -1;
            }

            if (companyA > companyB) {
              return 1;
            }

            return 0;
          }

          if (nameA > nameB) {
            if (companyA < companyB) {
              return -1;
            }

            if (companyA > companyB) {
              return 1;
            }

            return 0;
          }

          if (companyA < companyB) {
            return -1;
          }

          if (companyA > companyB) {
            return 1;
          }

          return 0;
        });

      setProgramUnitOptions(options);
      setLoadingProgramUnits(false);
    };

    const loadServiceItems = async () => {
      setLoadingServiceItems(true);
      const response = await getServiceItems();

      if (!isMountedRef.current) {
        return;
      }

      const options = response
        .filter((item) => {
          if (
            item.ccx_name.includes("Daily Attendance") ||
            item.ccx_name.includes("Drive Time") ||
            item.ccx_name.includes("Bed Day")
          ) {
            return false;
          }

          return true;
        })
        .map((item) => {
          let code = "";

          switch (item.ccx_code) {
            case "AL":
              code = "SAFY of Alabama";
              break;
            case "CO":
              code = "SAFY of Colorado";
              break;
            case "IN":
              code = "SAFY of Indiana";
              break;
            case "KY":
              code = "SAFY of Kentucky";
              break;
            case "NV":
              code = "SAFY of Nevada";
              break;
            case "OH":
              code = "SAFY of Ohio";
              break;
            case "OK":
              code = "SAFY of Oklahoma";
              break;
            case "SC":
              code = "SAFY of South Carolina";
              break;
            default:
              code = "Missing Code";
              break;
          }

          return {
            _id: `${item.ccx_code} - ${item.ccx_name}`,
            name: `${item.ccx_code} - ${item.ccx_name}`,
            code: code,
          };
        })
        .sort((a, b) => {
          const codeA = a.code.toUpperCase();
          const codeB = b.code.toUpperCase();
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();

          if (nameA < nameB) {
            if (codeA < codeB) {
              return -1;
            }

            if (codeA > codeB) {
              return 1;
            }

            return 0;
          }

          if (nameA > nameB) {
            if (codeA < codeB) {
              return -1;
            }

            if (codeA > codeB) {
              return 1;
            }

            return 0;
          }

          if (codeA < codeB) {
            return -1;
          }

          if (codeA > codeB) {
            return 1;
          }

          return 0;
        });

      setServiceItemOptions(options);
      setLoadingServiceItems(false);
    };

    loadProgramUnits();
    loadServiceItems();
  }, [userBusinessUnit]);

  const filterProgramUnits = (programUnit, searchString) => {
    const valueToCompare = (
      programUnit.name + programUnit.company
    ).toUpperCase();
    return valueToCompare.includes(searchString.toUpperCase());
  };

  const filterServiceItems = (serviceItem, searchString) => {
    const valueToCompare = (serviceItem.name + serviceItem.code).toUpperCase();
    return valueToCompare.includes(searchString.toUpperCase());
  };

  const requestTypeOptions = [
    { _id: "New Program", name: "New Program" },
    { _id: "Modality", name: "Modality" },
    { _id: "Service", name: "Service" },
    { _id: "Progress Note Template", name: "Progress Note Template" },
    { _id: "System Enhancement", name: "System Enhancement" },
  ];

  const programTypeOptions = [
    { _id: "Adoption", name: "Adoption" },
    { _id: "Behavioral Health", name: "Behavioral Health" },
    { _id: "Family Preservation", name: "Family Preservation" },
    { _id: "Independent Living", name: "Independent Living" },
    { _id: "TFC", name: "TFC" },
  ];

  const modalityOptions = [
    { _id: "Adding a new modality", name: "Adding a new modality" },
    {
      _id: "Expiring an existing modality",
      name: "Expiring an existing modality",
    },
  ];

  const unitsOptions = [
    { _id: "15", name: "15" },
    { _id: "30", name: "30" },
    { _id: "60", name: "60" },
    { _id: "90", name: "90" },
    { _id: "120", name: "120" },
  ];

  const serviceStatusOptions = [
    { _id: "Mental Health", name: "Mental Health" },
    { _id: "Bed Day", name: "Bed Day" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const handleSign = () => {
    let allSignatures = [...signatures];

    const sig = {
      datetime: new Date(),
      signatureType: "Provider",
      signatureRole: "Provider",
      userId: safyInfo.user.domainName,
      name: safyInfo.user.displayName,
    };

    allSignatures.push(sig);

    saveSignature(allSignatures);

    const databaseOrg =
      process.env.REACT_APP_ENVIRONMENT === "Production"
        ? "ReactLive"
        : "ReactDev";

    const logData = {
      Type: "Signature",
      Location: databaseOrg,
      Description: location.pathname,
      Data: JSON.stringify(sig),
      ObjectReferenceType: "System Change Request Signature",
      ObjectReferenceId: submissionid,
    };

    writeProcessLog(logData);
  };

  const getErrors = (errors, startingIndex = 0) => {
    return (
      <div className="p-2">
        Form cannot be signed until all required fields have a value.
        <br />
        <br />
        The following fields did not pass validation:
        <ul>
          {Object.keys(errors)
            .slice(startingIndex)
            .map((propName, index) => {
              if (index < 10) {
                return <li key={index}>{errors[propName]}</li>;
              } else {
                return null;
              }
            })}
        </ul>
        {Object.keys(errors).slice(startingIndex).length > 10 ? (
          <button
            className="btn btn-link text-white"
            onClick={() => {
              toast.error(getErrors(errors, startingIndex + 10));
            }}
          >
            More validation errors.
          </button>
        ) : null}
      </div>
    );
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <div className="bg-safy2-1 p-2 text-white rounded-top d-print-none">
          <div className="clearfix">
            <img
              src={logo}
              alt="logo"
              className="me-4"
              style={{ float: "left", width: "48px" }}
            ></img>
            <h3 style={{ float: "left" }}>{formsyConfig.FormDescription}</h3>
            {formsyDetails.Active === "1" ? (
              <strong className="d-print-none" style={{ float: "right" }}>
                {safyInfo.user.displayName}
              </strong>
            ) : null}
          </div>
          <div className="clearfix">
            {formsyDetails.ModifiedOn !== undefined ? (
              <strong className="d-print-none" style={{ float: "right" }}>
                {formsyDetails.Active === "1" ? "Last saved" : "Deactivated"} on{" "}
                {moment
                  .utc(formsyDetails.ModifiedOn)
                  .local()
                  .format("MMM D, YYYY [at] h:mm A")}{" "}
                by {formsyDetails.ModifiedByName}
              </strong>
            ) : (
              <strong
                className="d-print-none text-warning d-flex"
                style={{ float: "right" }}
              >
                <i className="fas fa-exclamation-triangle mt-1 me-1"></i>
                To prevent loss of work, please click save.
              </strong>
            )}
          </div>
        </div>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Select
                name="RequestType"
                label="Type of System Change Request:"
                error={errors.RequestType}
                value={getFieldValue(data.RequestType)}
                options={requestTypeOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="RequestName"
                label={data.RequestName["@label"]}
                error={errors.RequestName}
                value={getFieldValue(data.RequestName)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {getFieldValue(data.RequestType) === "New Program" ? (
                <ExpandDiv
                  id="NewProgram"
                  title="New Program"
                  expandStatus={expandStatus}
                >
                  <Input
                    name="ProgramEffectiveDate"
                    label="Effective Date"
                    error={errors.ProgramEffectiveDate}
                    value={getFieldValue(data.ProgramEffectiveDate)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                  <Input
                    name="ProgramName"
                    label="Program Name"
                    error={errors.ProgramName}
                    value={getFieldValue(data.ProgramName)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Form.Group>
                    <label htmlFor="ProgramDivisions">
                      Program being provided in following divisions:
                    </label>
                    <Multiselect
                      name="ProgramDivisions"
                      data={userDivisions}
                      dataKey="_id"
                      textField="name"
                      value={
                        data.ProgramDivisions["@value"]
                          ? JSON.parse(data.ProgramDivisions["@value"])
                          : []
                      }
                      onChange={(items) => {
                        handleMultiselectChange(
                          "ProgramDivisions",
                          JSON.stringify(items.map((item) => item._id))
                        );
                      }}
                      allowCreate={true}
                    />
                    {errors.ProgramDivisions && (
                      <div className="alert alert-danger">
                        {errors.ProgramDivisions}
                      </div>
                    )}
                  </Form.Group>
                  <TextArea
                    name="ProgramTeamMembers"
                    label="What Team Members need access to this program?"
                    error={errors.ProgramTeamMembers}
                    value={getFieldValue(data.ProgramTeamMembers)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Select
                    name="ProgramType"
                    label="Program Type:"
                    error={errors.ProgramType}
                    value={getFieldValue(data.ProgramType)}
                    options={programTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="ProgramBedRequirement"
                    label="Bed Requirement"
                    error={errors.ProgramBedRequirement}
                    value={getFieldValue(data.ProgramBedRequirement)}
                    options={yesNoOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="ProgramCarePlanning"
                    label={
                      <div className="h6">Eligible for Care Planning:</div>
                    }
                    error={errors.ProgramCarePlanning}
                    value={getFieldValue(data.ProgramCarePlanning)}
                    options={yesNoOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.ProgramCarePlanning) === "Yes" ? (
                    <>
                      Care Plan Libraries:
                      <br />
                      <TextArea
                        name="ProgramCarePlanCustomized"
                        label="Customized:"
                        error={errors.ProgramCarePlanCustomized}
                        value={getFieldValue(data.ProgramCarePlanCustomized)}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="ProgramCarePlanWiley"
                        label="Wiley:"
                        error={errors.ProgramCarePlanWiley}
                        value={getFieldValue(data.ProgramCarePlanWiley)}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Care Plan Configuration:
                      <TextArea
                        name="ProgramCarePlanConfigNarrative"
                        label="What Narrative Needs to be Added:"
                        error={errors.ProgramCarePlanConfigNarrative}
                        value={getFieldValue(
                          data.ProgramCarePlanConfigNarrative
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      Care Plan Components:
                      <div className="row">
                        <div className="col-4">
                          <Input
                            name="ProgramCarePlanProblemLabel"
                            label="Problem Label"
                            error={errors.ProgramCarePlanProblemLabel}
                            value={getFieldValue(
                              data.ProgramCarePlanProblemLabel
                            )}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanProblemReviewRequired"
                            label="Problem Review Required"
                            error={errors.ProgramCarePlanProblemReviewRequired}
                            value={getFieldValue(
                              data.ProgramCarePlanProblemReviewRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Input
                            name="ProgramCarePlanGoalLabel"
                            label="Goal Label"
                            error={errors.ProgramCarePlanGoalLabel}
                            value={getFieldValue(data.ProgramCarePlanGoalLabel)}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanGoalReviewRequired"
                            label="Goal Review Required"
                            error={errors.ProgramCarePlanGoalReviewRequired}
                            value={getFieldValue(
                              data.ProgramCarePlanGoalReviewRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanGoalProgressRequired"
                            label="Goal Progress Required"
                            error={errors.ProgramCarePlanGoalProgressRequired}
                            value={getFieldValue(
                              data.ProgramCarePlanGoalProgressRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Input
                            name="ProgramCarePlanObjectiveLabel"
                            label="Objective Label"
                            error={errors.ProgramCarePlanObjectiveLabel}
                            value={getFieldValue(
                              data.ProgramCarePlanObjectiveLabel
                            )}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanObjectiveReviewRequired"
                            label="Objective Review Required"
                            error={
                              errors.ProgramCarePlanObjectiveReviewRequired
                            }
                            value={getFieldValue(
                              data.ProgramCarePlanObjectiveReviewRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4">
                          <Input
                            name="ProgramCarePlanInterventionLabel"
                            label="Intervention Label"
                            error={errors.ProgramCarePlanInterventionLabel}
                            value={getFieldValue(
                              data.ProgramCarePlanInterventionLabel
                            )}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanInterventionReviewRequired"
                            label="Intervention Review Required"
                            error={
                              errors.ProgramCarePlanInterventionReviewRequired
                            }
                            value={getFieldValue(
                              data.ProgramCarePlanInterventionReviewRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ProgramCarePlanInterventionProgressRequired"
                            label="Intervention Progress Required"
                            error={
                              errors.ProgramCarePlanInterventionProgressRequired
                            }
                            value={getFieldValue(
                              data.ProgramCarePlanInterventionProgressRequired
                            )}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      Care Plan Frequency
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequencyInitial"
                            label="Initial"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequencyInitial
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency30Day"
                            label="30 Day"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency30Day
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency60Day"
                            label="60 Day"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency60Day
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency90Day"
                            label="90 Day"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency90Day
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency6Months"
                            label="6 Months"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency6Months
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency180Days"
                            label="180 Days"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency180Days
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequency270Days"
                            label="270 Days"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequency270Days
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequencyAnnual"
                            label="Annual"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequencyAnnual
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequencyUpdate"
                            label="Update"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequencyUpdate
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="ProgramCarePlanFrequencyDischarge"
                            label="Discharge"
                            checked={
                              getFieldValue(
                                data.ProgramCarePlanFrequencyDischarge
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divClassName="ms-3"
                          />
                        </div>
                      </div>
                      <Input
                        name="ProgramCarePlanMaximumReviewInterval"
                        label="Maximum Review Interval (Days)"
                        error={errors.ProgramCarePlanMaximumReviewInterval}
                        value={getFieldValue(
                          data.ProgramCarePlanMaximumReviewInterval
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  ) : null}
                  <div className="h6">Assessments &amp; Forms</div>
                  <TextArea
                    name="ProgramAssessments"
                    error={errors.ProgramAssessments}
                    value={getFieldValue(data.ProgramAssessments)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <div className="h6">Reporting</div>
                  <TextArea
                    name="ProgramReportingNeeds"
                    label="What reporting needs are there?"
                    error={errors.ProgramReportingNeeds}
                    value={getFieldValue(data.ProgramReportingNeeds)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <TextArea
                    name="ProgramPayor"
                    label="Who is the payor for this program? (include name, address, and phone)"
                    error={errors.ProgramPayor}
                    value={getFieldValue(data.ProgramPayor)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <TextArea
                    name="ProgramNotes"
                    label="Notes/Comments:"
                    error={errors.ProgramNotes}
                    value={getFieldValue(data.ProgramNotes)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </ExpandDiv>
              ) : null}
              {getFieldValue(data.RequestType) === "New Program" ||
              getFieldValue(data.RequestType) === "Modality" ? (
                <ExpandDiv
                  id="Modality"
                  title="Modality"
                  expandStatus={expandStatus}
                >
                  {getFieldValue(data.RequestType) === "New Program" ? (
                    <Input
                      name="CurrentModalities"
                      label={data.CurrentModalities["@label"]}
                      error={errors.CurrentModalities}
                      value={getFieldValue(data.CurrentModalities)}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                  <RadioButton
                    name="ModalityRequestType"
                    error={errors.ModalityRequestType}
                    value={getFieldValue(data.ModalityRequestType)}
                    options={modalityOptions}
                    optionsOnNewLine={true}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.ModalityRequestType["@value"] ? (
                    <>
                      {data.ModalityRequestType["@value"] ===
                      "Adding a new modality" ? (
                        <>
                          <FieldTable
                            name="ModalityNewDetails"
                            commentsHandler={handleComments}
                          >
                            {JSON.parse(data.ModalityNewDetails["@value"]).map(
                              (item, index) => (
                                <React.Fragment
                                  key={`ModalityNewDetails-${index}`}
                                >
                                  {item.Name ? (
                                    <div className="h6 bg-safy2-5 border border-dark p-2">
                                      {item.Name}
                                    </div>
                                  ) : null}
                                  <div className="row">
                                    <div className="col">
                                      <Input
                                        name="Name"
                                        label="Modality Name:"
                                        value={item.Name}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "ModalityNewDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Input
                                        name="EffectiveDate"
                                        label="Effective Date:"
                                        value={item.EffectiveDate}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "ModalityNewDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                        type="date"
                                      />
                                    </div>
                                  </div>
                                  {data.RequestType["@value"] !==
                                  "New Program" ? (
                                    <Form.Group>
                                      <label htmlFor="ProgramUnits">
                                        Programs/Divisions needing modality:
                                      </label>
                                      <Multiselect
                                        name="ProgramUnits"
                                        data={programUnitOptions}
                                        dataKey="_id"
                                        textField="name"
                                        value={item.ProgramUnits}
                                        onChange={(programUnits) => {
                                          const input = {
                                            currentTarget: {
                                              name: "ProgramUnits",
                                              value: programUnits.map(
                                                (a) => a._id
                                              ),
                                              type: "multiselect",
                                            },
                                          };
                                          handleRowChange(
                                            input,
                                            index,
                                            "ModalityNewDetails"
                                          );
                                        }}
                                        busy={loadingProgramUnits}
                                        filter={filterProgramUnits}
                                        groupBy="company"
                                      />
                                    </Form.Group>
                                  ) : null}
                                  <Input
                                    name="Reason"
                                    label="Reason for modality to be added:"
                                    value={item.Reason}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "ModalityNewDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </React.Fragment>
                              )
                            )}
                            {!formDisabled ? (
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddRow("ModalityNewDetails", {
                                    EffectiveDate: moment().format(
                                      moment.HTML5_FMT.DATE
                                    ),
                                  });
                                }}
                              >
                                Add New Modality
                              </button>
                            ) : null}
                            {errors.ModalityNewDetails !== undefined ? (
                              <div className="row">
                                <div className="col">
                                  <div className="alert alert-danger">
                                    {errors.ModalityNewDetails}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </FieldTable>
                        </>
                      ) : (
                        <>
                          <div className="row">
                            <div className="col">
                              <Input
                                name="ModalityNameToExpire"
                                label="Modality Name:"
                                error={errors.ModalityNameToExpire}
                                value={getFieldValue(data.ModalityNameToExpire)}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Input
                                name="ModalityExpirationDate"
                                label="Expiration Date:"
                                error={errors.ModalityExpirationDate}
                                value={getFieldValue(
                                  data.ModalityExpirationDate
                                )}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <Form.Group>
                            <label htmlFor="ModalityProgramsToExpire">
                              Programs/Divisions modality needs removed from:
                            </label>
                            <Multiselect
                              name="ModalityProgramsToExpire"
                              data={programUnitOptions}
                              dataKey="_id"
                              textField="name"
                              value={
                                data.ModalityProgramsToExpire["@value"]
                                  ? JSON.parse(
                                      data.ModalityProgramsToExpire["@value"]
                                    )
                                  : []
                              }
                              onChange={(items) => {
                                handleMultiselectChange(
                                  "ModalityProgramsToExpire",
                                  JSON.stringify(items.map((item) => item._id))
                                );
                              }}
                              busy={loadingProgramUnits}
                              filter={filterProgramUnits}
                              groupBy="company"
                            />
                          </Form.Group>
                        </>
                      )}
                    </>
                  ) : null}
                  <TextArea
                    name="ModalityNotes"
                    label="Notes/Comments:"
                    error={errors.ModalityNotes}
                    value={getFieldValue(data.ModalityNotes)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </ExpandDiv>
              ) : null}
              {getFieldValue(data.RequestType) === "New Program" ||
              getFieldValue(data.RequestType) === "Service" ? (
                <ExpandDiv
                  id="ServiceItem"
                  title="Service Item"
                  expandStatus={expandStatus}
                >
                  {getFieldValue(data.RequestType) === "New Program" ? (
                    <>
                      <Input
                        name="CurrentServiceItems"
                        label={data.CurrentServiceItems["@label"]}
                        error={errors.CurrentServiceItems}
                        value={getFieldValue(data.CurrentServiceItems)}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="ServiceItemNeeded"
                        label={data.ServiceItemNeeded["@label"]}
                        checked={
                          getFieldValue(data.ServiceItemNeeded) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </>
                  ) : null}
                  {getFieldValue(data.RequestType) === "Service" ||
                  getFieldValue(data.ServiceItemNeeded) === "true" ? (
                    <FieldTable
                      name="ServiceItems"
                      commentsHandler={handleComments}
                    >
                      {JSON.parse(data.ServiceItems["@value"]).map(
                        (item, index) => (
                          <React.Fragment key={`ServiceItems-${index}`}>
                            {" "}
                            {item.Name ? (
                              <div className="h6 bg-safy2-5 border border-dark p-2">
                                {item.Name}
                              </div>
                            ) : null}
                            <div className="row">
                              <div className="col">
                                <Input
                                  name="Name"
                                  label="Service Item Name:"
                                  value={item.Name}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Input
                                  name="EffectiveDate"
                                  label="Effective Date:"
                                  value={item.EffectiveDate}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                  type="date"
                                />
                              </div>
                              <div className="col">
                                <Input
                                  name="BillingCode"
                                  label="Service Billing Code:"
                                  value={item.BillingCode}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Form.Group>
                                  <label htmlFor="ProgramUnits">
                                    Programs/Divisions needing modality:
                                  </label>
                                  <Multiselect
                                    name="ProgramUnits"
                                    data={programUnitOptions}
                                    dataKey="_id"
                                    textField="name"
                                    value={item.ProgramUnits}
                                    onChange={(programUnits) => {
                                      const input = {
                                        currentTarget: {
                                          name: "ProgramUnits",
                                          value: programUnits.map((a) => a._id),
                                          type: "multiselect",
                                        },
                                      };
                                      handleRowChange(
                                        input,
                                        index,
                                        "ServiceItems"
                                      );
                                    }}
                                    busy={loadingProgramUnits}
                                    filter={filterProgramUnits}
                                    groupBy="company"
                                  />
                                </Form.Group>
                              </div>
                            </div>
                            <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                              <div className="col">
                                <RadioButton
                                  name="PerEncounter"
                                  label="Per Encounter:"
                                  value={item.PerEncounter}
                                  options={yesNoOptions}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      value: e.currentTarget.value,
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              {item.PerEncounter !== "Yes" ? (
                                <div className="col">
                                  <Select
                                    name="UnitsMeasured"
                                    label="Units Measured (minutes):"
                                    value={item.UnitsMeasured}
                                    options={unitsOptions}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    disabled={formDisabled}
                                  />
                                </div>
                              ) : null}
                              <div className="col">
                                <RadioButton
                                  name="NonBillable"
                                  label="Non-Billable:"
                                  value={item.NonBillable}
                                  options={yesNoOptions}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      value: e.currentTarget.value,
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Input
                                  name="Rate"
                                  label="Rate:"
                                  value={item.Rate}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <RadioButton
                                  name="VariableUCR"
                                  label="Variable UCR:"
                                  value={item.VariableUCR}
                                  options={yesNoOptions}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      value: e.currentTarget.value,
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <RadioButton
                                  name="BedAssigned"
                                  label="Bed Assigned:"
                                  value={item.BedAssigned}
                                  options={yesNoOptions}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      value: e.currentTarget.value,
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <TextArea
                                  name="Payor"
                                  label="Who is the responsible payor for this service? (include name, address, and phone)"
                                  value={item.Payor}
                                  onChange={(e) => {
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <RadioButton
                                  name="PreventOverlap"
                                  label="Prevent Overlap:"
                                  value={item.PreventOverlap}
                                  options={yesNoOptions}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      value: e.currentTarget.value,
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              {item.PreventOverlap === "Yes" ? (
                                <div className="col">
                                  <RadioButton
                                    name="SuppressWarnings"
                                    label="If yes, Suppress Overlap Warnings:"
                                    value={item.SuppressWarnings}
                                    options={yesNoOptions}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    disabled={formDisabled}
                                  />
                                </div>
                              ) : null}
                            </div>
                            <div className="row">
                              <div className="col">
                                Delivery Method:
                                <div className="col">
                                  <Checkbox
                                    name="VideoTeleconferencing"
                                    label="Video Teleconferencing"
                                    checked={
                                      item.VideoTeleconferencing === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="OtherDeliveryMethod"
                                    label="Other"
                                    checked={
                                      item.OtherDeliveryMethod === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Email"
                                    label="Email"
                                    checked={item.Email === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Text"
                                    label="Text"
                                    checked={item.Text === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="FaceToFace"
                                    label="Face To Face"
                                    checked={item.FaceToFace === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Telephone"
                                    label="Telephone"
                                    checked={item.Telephone === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                              </div>
                              <div className="col">
                                Place of Service:
                                <div className="col">
                                  <Checkbox
                                    name="Telehealth"
                                    label="Telehealth (Not for Ohio)"
                                    checked={item.Telehealth === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Office"
                                    label="Office"
                                    checked={item.Office === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Home"
                                    label="Home"
                                    checked={item.Home === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="OtherPlaceOfService"
                                    label="Other Unlisted Facility"
                                    checked={
                                      item.OtherPlaceOfService === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="School"
                                    label="School"
                                    checked={item.School === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="CommunityMHCenter"
                                    label="Community Mental Health Center"
                                    checked={item.CommunityMHCenter === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Residential"
                                    label="Residential"
                                    checked={item.Residential === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Hospital"
                                    label="Hospital"
                                    checked={item.Hospital === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Facility"
                                    label="Facility"
                                    checked={item.Facility === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Community"
                                    label="Community"
                                    checked={item.Community === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                              </div>
                              <div className="col">
                                Recipient Code:
                                <div className="col">
                                  <Checkbox
                                    name="Individual"
                                    label="Individual (SC only)"
                                    checked={item.Individual === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="GroupRecipientCode"
                                    label="Group"
                                    checked={item.GroupRecipientCode === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="ClientOnly"
                                    label="Client Only"
                                    checked={item.ClientOnly === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="ClientAndFamily"
                                    label="Client And Family Members"
                                    checked={item.ClientAndFamily === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="ClientCollateralsFamily"
                                    label="Client / Collaterals / Family"
                                    checked={
                                      item.ClientCollateralsFamily === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="FamilyOnly"
                                    label="Family Members Only"
                                    checked={item.FamilyOnly === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="StaffOnly"
                                    label="Staff Only"
                                    checked={item.StaffOnly === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="CollateralsOnly"
                                    label="Collaterals Only"
                                    checked={item.CollateralsOnly === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="CollateralsFamily"
                                    label="Collaterals and Family"
                                    checked={item.CollateralsFamily === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="ClientAndCollaterals"
                                    label="Client and Collaterals"
                                    checked={
                                      item.ClientAndCollaterals === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                              </div>
                            </div>
                            <RadioButton
                              name="ServiceStatus"
                              label="Service Status:"
                              value={item.ServiceStatus}
                              options={serviceStatusOptions}
                              onChange={(e) => {
                                e.currentTarget = {
                                  name: e.currentTarget.name.split("-")[0],
                                  value: e.currentTarget.value,
                                };
                                handleRowChange(e, index, "ServiceItems");
                              }}
                              disabled={formDisabled}
                            />
                            {item.ServiceStatus === "Mental Health" ? (
                              <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                                <div className="col">
                                  <Checkbox
                                    name="Attended"
                                    label="Attended"
                                    checked={item.Attended === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="AttentedSupervision"
                                    label="Attended with Direct Supervision"
                                    checked={
                                      item.AttentedSupervision === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="CancelledByClient"
                                    label="Cancelled by Client"
                                    checked={item.CancelledByClient === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="CancelledByProvider"
                                    label="Cancelled By Provider"
                                    checked={
                                      item.CancelledByProvider === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="GroupServiceStatus"
                                    label="Group"
                                    checked={item.GroupServiceStatus === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="NoShow"
                                    label="No Show"
                                    checked={item.NoShow === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                              </div>
                            ) : null}
                            {item.ServiceStatus === "Bed Day" ? (
                              <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                                <div className="col">
                                  <Checkbox
                                    name="Occupied"
                                    label="Occupied"
                                    checked={item.Occupied === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Respite"
                                    label="Respite"
                                    checked={item.Respite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="DoublePaidRespite"
                                    label="Double Paid Respite"
                                    checked={item.DoublePaidRespite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="UnpaidRespite"
                                    label="Unpaid Respite"
                                    checked={item.UnpaidRespite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="PaidLeave"
                                    label="Paid Leave"
                                    checked={item.PaidLeave === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="UnpaidLeave"
                                    label="Unpaid Leave"
                                    checked={item.UnpaidLeave === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="Discharged"
                                    label="Discharged"
                                    checked={item.Discharged === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="DayOnlyRespite"
                                    label="Day Only Respite"
                                    checked={item.DayOnlyRespite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="DayOnlyDoublePayRespite"
                                    label="Day Only Double Pay Respite"
                                    checked={
                                      item.DayOnlyDoublePayRespite === "true"
                                    }
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="FHDayOnlyRespite"
                                    label="Foster Home Day Only Respite"
                                    checked={item.FHDayOnlyRespite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="FHRespite"
                                    label="Foster Home Respite (CO)"
                                    checked={item.FHRespite === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="PlacementPending"
                                    label="Placement Pending"
                                    checked={item.PlacementPending === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="UnpaidDayOnly"
                                    label="Unpaid Day Only (CO)"
                                    checked={item.UnpaidDayOnly === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                                <div className="col">
                                  <Checkbox
                                    name="BedHold"
                                    label="Bed Hold"
                                    checked={item.BedHold === "true"}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        checked: e.currentTarget.checked,
                                        type: "checkbox",
                                      };
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                    divClassName="ms-3"
                                  />
                                </div>
                              </div>
                            ) : null}
                            What type of degree is able to provide this service?
                            <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2">
                              <div className="col">
                                <Checkbox
                                  name="BABS"
                                  label="BA/BS"
                                  checked={item.BABS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LPCC"
                                  label="LPCC"
                                  checked={item.LPCC === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="BABSCANS"
                                  label="BA/BS-CANS"
                                  checked={item.BABSCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LPCCANS"
                                  label="LPC-CANS"
                                  checked={item.LPCCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FosterParent"
                                  label="Foster Parent"
                                  checked={item.FosterParent === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LPCCCANS"
                                  label="LPCC-CANS"
                                  checked={item.LPCCCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LBP"
                                  label="LBP"
                                  checked={item.LBP === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LPN"
                                  label="LPN"
                                  checked={item.LPN === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LBSW"
                                  label="LBSW"
                                  checked={item.LBSW === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LSW"
                                  label="LSW"
                                  checked={item.LSW === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LCDC"
                                  label="LCDC"
                                  checked={item.LCDC === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LSWCANS"
                                  label="LSW-CANS"
                                  checked={item.LSWCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LCSW"
                                  label="LCSW"
                                  checked={item.LCSW === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="MastersCANS"
                                  label="Masters-CANS"
                                  checked={item.MastersCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LCSWCANS"
                                  label="LCSW-CANS"
                                  checked={item.LCSWCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Masters"
                                  label="Masters"
                                  checked={item.Masters === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LGSW"
                                  label="LGSW"
                                  checked={item.LGSW === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="MD"
                                  label="MD"
                                  checked={item.MD === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LicTrainee"
                                  label="Lic Trainee"
                                  checked={item.LicTrainee === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Pharmacist"
                                  label="Pharmacist"
                                  checked={item.Pharmacist === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LICDC"
                                  label="LICDC"
                                  checked={item.LICDC === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PhD"
                                  label="PhD"
                                  checked={item.PhD === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LIMFT"
                                  label="LIMFT"
                                  checked={item.LIMFT === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="QMHS"
                                  label="QMHS"
                                  checked={item.QMHS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LISWCANS"
                                  label="LISW-CANS"
                                  checked={item.LISWCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LISW"
                                  label="LISW"
                                  checked={item.LISW === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="QMHSCANS"
                                  label="QMHS-CANS"
                                  checked={item.QMHSCANS === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="RN"
                                  label="RN"
                                  checked={item.RN === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LMFT"
                                  label="LMFT"
                                  checked={item.LMFT === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Unlicensed"
                                  label="Unlicensed"
                                  checked={item.Unlicensed === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LPC"
                                  label="LPC"
                                  checked={item.LPC === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OtherDegree"
                                  label="Other Degree"
                                  checked={item.OtherDegree === "true"}
                                  onChange={(e) => {
                                    e.currentTarget = {
                                      name: e.currentTarget.name.split("-")[0],
                                      checked: e.currentTarget.checked,
                                      type: "checkbox",
                                    };
                                    handleRowChange(e, index, "ServiceItems");
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                  divClassName="ms-3"
                                />
                                {item.OtherDegree === "true" ? (
                                  <Input
                                    name="OtherDegreeDetails"
                                    value={item.OtherDegreeDetails}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "ServiceItems");
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                            <RadioButton
                              name="ConcurrentServices"
                              label="Can any other services be provided at the same time as this one, concurrent?"
                              value={item.ConcurrentServices}
                              options={yesNoOptions}
                              onChange={(e) => {
                                e.currentTarget = {
                                  name: e.currentTarget.name.split("-")[0],
                                  value: e.currentTarget.value,
                                };
                                handleRowChange(e, index, "ServiceItems");
                              }}
                              disabled={formDisabled}
                            />
                            {item.ConcurrentServices === "Yes" ? (
                              <Multiselect
                                name="ConcurrentServicesList"
                                data={serviceItemOptions}
                                dataKey="_id"
                                textField="name"
                                value={item.ConcurrentServicesList || []}
                                onChange={(serviceItems) => {
                                  const input = {
                                    currentTarget: {
                                      name: "ConcurrentServicesList",
                                      value: serviceItems.map((a) => a._id),
                                      type: "multiselect",
                                    },
                                  };
                                  handleRowChange(input, index, "ServiceItems");
                                }}
                                busy={loadingServiceItems}
                                filter={filterServiceItems}
                                groupBy="code"
                              />
                            ) : null}
                            <TextArea
                              name="ProgressNoteTemplate"
                              label="What should your progress note template look like or would you like to use one already created?"
                              value={item.ProgressNoteTemplate}
                              onChange={(e) => {
                                handleRowChange(e, index, "ServiceItems");
                              }}
                              disabled={formDisabled}
                              rows="5"
                            />
                          </React.Fragment>
                        )
                      )}
                      {!formDisabled ? (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("ServiceItems", {
                              EffectiveDate: moment().format(
                                moment.HTML5_FMT.DATE
                              ),
                            });
                          }}
                        >
                          Add Service Item
                        </button>
                      ) : null}
                      {errors.ServiceItems !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.ServiceItems}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                  ) : null}

                  <TextArea
                    name="ServiceItemNotes"
                    label="Notes/Comments:"
                    error={errors.ServiceItemNotes}
                    value={getFieldValue(data.ServiceItemNotes)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </ExpandDiv>
              ) : null}
              {getFieldValue(data.RequestType) === "Progress Note Template" ? (
                <ExpandDiv
                  id="ProgressNoteTemplate"
                  title="Progress Note"
                  expandStatus={expandStatus}
                >
                  <FieldTable
                    name="ProgressNoteTemplates"
                    commentsHandler={handleComments}
                  >
                    {JSON.parse(data.ProgressNoteTemplates["@value"]).map(
                      (item, index) => (
                        <React.Fragment key={`ProgressNoteTemplates-${index}`}>
                          <TextArea
                            name="Template"
                            label="What should your progress note template look like or would you like to use one already created?"
                            value={item.Template}
                            onChange={(e) => {
                              handleRowChange(
                                e,
                                index,
                                "ProgressNoteTemplates"
                              );
                            }}
                            disabled={formDisabled}
                            rows="5"
                          />
                          What Service Items need this new template?
                          <Multiselect
                            name="ServiceItems"
                            data={serviceItemOptions}
                            dataKey="_id"
                            textField="name"
                            value={item.ServiceItems || []}
                            onChange={(serviceItems) => {
                              const input = {
                                currentTarget: {
                                  name: "ServiceItems",
                                  value: serviceItems.map((a) => a._id),
                                  type: "multiselect",
                                },
                              };
                              handleRowChange(
                                input,
                                index,
                                "ProgressNoteTemplates"
                              );
                            }}
                            busy={loadingServiceItems}
                            filter={filterServiceItems}
                            groupBy="code"
                          />
                        </React.Fragment>
                      )
                    )}
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("ProgressNoteTemplates", {
                            EffectiveDate: moment().format(
                              moment.HTML5_FMT.DATE
                            ),
                          });
                        }}
                      >
                        Add Progress Note Template
                      </button>
                    ) : null}
                    {errors.ProgressNoteTemplates !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.ProgressNoteTemplates}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </FieldTable>
                  <TextArea
                    name="ProgressNoteTemplateNotes"
                    label="Notes/Comments:"
                    error={errors.ProgressNoteTemplateNotes}
                    value={getFieldValue(data.ProgressNoteTemplateNotes)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </ExpandDiv>
              ) : null}
              {getFieldValue(data.RequestType) === "System Enhancement" ? (
                <ExpandDiv
                  id="SystemEnhancement"
                  title="System Enhancement"
                  expandStatus={expandStatus}
                >
                  <TextArea
                    name="EnhancementRequest"
                    label="Please describe in detail the enhancement you would like to see:"
                    value={getFieldValue(data.EnhancementRequest)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    rows="5"
                  />
                </ExpandDiv>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={async () => {
                const validateResults = validate();
                if (Object.keys(validateResults).length === 0) {
                  handleSign();
                } else {
                  toast.error(getErrors(validateResults));
                }
              }}
            >
              Save and Sign
            </div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              signatures={signatures}
              onSign={saveSignature}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SystemChangeRequest;
